import { inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Route, Router } from "@angular/router";

import { productRoutes } from "./components/ticket-forms/products";
import { cachedEligibilityGuard } from "./guards/cached-eligibility.guard";
import { MembershipsService, PackagesService } from "../admin";

import { WARM_OFFLINE } from "src/app/core/offline/route-warm.service";
import { EmptyDialogComponent } from "src/app/shared";
import { AuthService, EventServicesCodes, loaderContext, ParentServiceCode, TourServicesCodes } from "src/app/core";
import { Permissions } from "src/assets/config/config";

interface ParentServiceRoutes extends Route {
  path: keyof typeof ParentServiceCode;
}

interface TourServiceRoutes extends Route {
  path: keyof typeof TourServicesCodes;
}

interface EventServiceRoutes extends Route {
  path: keyof typeof EventServicesCodes;
}

// -----------------------------------------------------------------------------------------------

/**
 * @description children routes of the tour services
 */
const childrenTourBookRoutes: TourServiceRoutes[] = [
  {
    path: "EntranceTicket",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () => import("./components/ticket-forms/regular-ticket/regular-ticket.component").then((c) => c.RegularTicketComponent),
  },
  // {
  //   path: "FreeEntry",
  //   canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
  //   loadComponent: () => import("./components/ticket-forms/free-entry/free-entry.component").then((c) => c.FreeEntryComponent),
  // },
  // {
  //   path: "GroupOffers",
  //   canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
  //   loadComponent: () => import("./components/ticket-forms/group-offers/group-offers.component").then((c) => c.GroupOffersComponent),
  // },
  {
    path: "SchoolUniversityVisits",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () =>
      import("./components/ticket-forms/student-visit-ticket/student-visit-ticket.component").then((c) => c.StudentVisitTicketComponent),
  },
  {
    path: "Packages",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    canActivate: [
      () => {
        const dialog = inject(MatDialog);
        const packagesService = inject(PackagesService);

        return cachedEligibilityGuard({
          // `loaderContext` composes with the cache policy inside `getAll()` rather than replacing
          // it — the reason T-09 made the context helpers take an existing context.
          fetch: () => packagesService.getAll(loaderContext("packages")),
          allow: (packages) => packages.length > 0,
          onDenied: () =>
            dialog.open(EmptyDialogComponent, {
              panelClass: ["small", "not-fit-content", "h-max-content", "p-0"],
              autoFocus: false,
              disableClose: true,
              data: {
                message: "ALERT.PACKAGES_TOUR_EMPTY",
              },
            }),
        });
      },
    ],
    loadComponent: () => import("./components/ticket-forms/packages/packages.component").then((c) => c.PackagesComponent),
  },
  {
    path: TourServicesCodes.GuidedTours,
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () => import("./components/ticket-forms/guided-tours/guided-tours.component").then((c) => c.GuidedToursComponent),
  },
  {
    path: "TourismCompanies",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    canActivate: [
      () => {
        const router = inject(Router);
        const authService = inject(AuthService);
        const membershipsService = inject(MembershipsService);

        return cachedEligibilityGuard({
          fetch: () => membershipsService.checkAddingService(),
          allow: (status) => status === 1 && !authService.isCompanyUser(),
          onDenied: () => router.navigate(["/services"]),
        });
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/tourism-companies/tourism-companies.component").then((c) => c.TourismCompaniesComponent),
  },
  {
    path: "OfficialInstitutions",
    canActivate: [
      () => {
        const auth = inject(AuthService);
        const isAllow = auth.isOfficialInstitutionUser() || auth.isCounter() || auth.isAdmin();
        if (isAllow) return isAllow;

        inject(Router).navigate(["/services"]);
        return isAllow;
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/official-institutions/official-institutions.component").then(
        (c) => c.OfficialInstitutionsComponent,
      ),
  },
  {
    path: TourServicesCodes.TourismCompanyEntryTicket,
    canActivate: [
      () => {
        const auth = inject(AuthService);
        const isAllow = auth.isCounter() || Permissions.ServicesManagement.includes(auth.user()?.RoleCode!);
        if (isAllow) return isAllow;

        inject(Router).navigate(["/services"]);
        return isAllow;
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/tourism-company-entry-ticket/tourism-company-entry-ticket.component").then(
        (c) => c.TourismCompanyEntryTicketComponent,
      ),
  },
  {
    path: TourServicesCodes.TourismCompanyGuidedTours,
    canActivate: [
      () => {
        const auth = inject(AuthService);
        const isAllow = auth.isCounter() || Permissions.ServicesManagement.includes(auth.user()?.RoleCode!);
        if (isAllow) return isAllow;

        inject(Router).navigate(["/services"]);
        return isAllow;
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/tourism-company-guided-tours/tourism-company-guided-tours.component").then(
        (c) => c.TourismCompanyGuidedToursComponent,
      ),
  },
  {
    path: "FriendsOfOmanMuseum",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    canActivate: [
      () => {
        const router = inject(Router);
        const membershipsService = inject(MembershipsService);

        return cachedEligibilityGuard({
          fetch: () => membershipsService.checkAddingService(),
          allow: (status) => status === 1,
          onDenied: () => router.navigate(["/services"]),
        });
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/friends-of-oman-museum/friends-of-oman-museum.component").then(
        (c) => c.FriendsOfOmanMuseumComponent,
      ),
  },
  {
    path: "Products",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    children: productRoutes,
  },
  {
    path: "FriendsOfOmanMuseum",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () =>
      import("./components/ticket-forms/friends-of-oman-museum/friends-of-oman-museum.component").then(
        (c) => c.FriendsOfOmanMuseumComponent,
      ),
  },
];

/**
 * @description children routes of the event services
 */
const childrenEventBookRoutes: EventServiceRoutes[] = [
  {
    path: "ExhibitionsAndEvents",
    children: [
      {
        path: "hold-event",
        title: "SERVICES.ExhibitionsAndEvents",
        loadComponent: () =>
          import("./components/ticket-forms/exhibitions-and-events-hold/exhibitions-and-events-hold.component").then(
            (c) => c.ExhibitionsAndEventsHoldComponent,
          ),
      },
      {
        path: "hold-event/:id",
        title: "SERVICES.ExhibitionsAndEvents",
        loadComponent: () =>
          import("./components/ticket-forms/exhibitions-and-events-hold/exhibitions-and-events-hold.component").then(
            (c) => c.ExhibitionsAndEventsHoldComponent,
          ),
      },
      {
        path: ":id",
        loadComponent: () =>
          import("./components/ticket-forms/exhibitions-and-events/exhibitions-and-events.component").then(
            (c) => c.ExhibitionsAndEventsComponent,
          ),
      },
    ],
  },
];

/**
 * @description main services categories routes.
 */
export const bookRoutes: ParentServiceRoutes[] = [
  {
    path: "TOUR",
    // Warmed as a subtree (T-11/T-17): the flag is inherited, so this covers the booking shell and
    // every ticket form under it — including the three whose bookings the outbox will accept. EVENT
    // is deliberately left out: its bookings cannot be queued offline (`notTour`), so downloading its
    // code ahead of time buys a clerk nothing they can finish.
    data: { [WARM_OFFLINE]: true },
    loadComponent: () => import("./pages/book/book.component").then((c) => c.BookComponent),
    children: childrenTourBookRoutes,
  },
  {
    path: "EVENT",
    loadComponent: () => import("./pages/book/book.component").then((c) => c.BookComponent),
    children: childrenEventBookRoutes,
  },
];
